import { createSlice } from '@reduxjs/toolkit';
import { getIncomers, getOutgoers } from '@xyflow/react';
import dagre from 'dagre';
import _compact from 'lodash/compact';
import _findLastIndex from 'lodash/findLastIndex';
import _max from 'lodash/max';
import _maxBy from 'lodash/maxBy';
import _minBy from 'lodash/minBy';
import moment from 'moment';
import i18n from '../app/i18n';
import {
  ConditionsClickType,
  ConditionsType,
  CustomerConditionType,
  EdgeType,
  FacebookTagType,
  GATriggerType,
  MANodeType,
  MAStatusType,
  ModalType,
  NodeType,
  PlatformType,
  ProviderActionType,
  ProviderType,
  ScheduleType,
  SnackBarType,
  TriggerTimeType,
  TriggerType,
  UrlFilterType,
} from '../app/types';
import {
  randomText,
  validateDate,
  validateHttpsAndHttpPath,
  validateMessages,
  validateText,
} from '../app/utils';
import MainService from '../services/MainService';
import errorHandle from './errorHandle';
import { openModal } from './modalSlice';
import { enqueueSnackbar } from './notifierSlice';

const startId = randomText();
const triggerId = randomText();
const endId = randomText();

// 只放MA的資料！！！
export const mainSlice = createSlice({
  name: 'main',
  initialState: {
    data: {
      name: `自動旅程 ${moment().format('YYYY-MM-DD HH:mm')}`,
      enabled: true,
      fbTag: FacebookTagType.NO_TAG,
      platform: PlatformType.LINE,
      startTime: moment().add(7, 'days').format('YYYY-MM-DD 00:00'),
      endTime: moment().add(21, 'days').format('YYYY-MM-DD 23:59'),
      limits: {
        message: undefined,
        per_customer: 1,
      },
      oos: {
        enabled: false,
        hour: 22,
        minute: 0,
        duration: 12 * 3600,
      },
      nodes: [
        {
          id: startId,
          type: NodeType.START,
          position: {
            x: 78.00067467707265,
            y: 0,
          },
          targetPosition: 'top',
          sourcePosition: 'bottom',
        },
        {
          id: triggerId,
          type: NodeType.TRIGGER,
          data: {
            type: TriggerType.ADD,
          },
          position: {
            x: 0.0009322678690461999,
            y: 107,
          },
          targetPosition: 'top',
          sourcePosition: 'bottom',
        },
        {
          id: endId,
          type: NodeType.END,
          position: {
            x: 86.00038895787371,
            y: 275,
          },
          targetPosition: 'top',
          sourcePosition: 'bottom',
        },
      ],
      edges: [
        {
          id: randomText(),
          source: startId,
          target: triggerId,
          type: 'smoothstep',
        },
        {
          id: randomText(),
          source: triggerId,
          target: endId,
          type: 'add',
        },
      ],
    },
    selectedNode: null,
    highlightPathNodes: [],
    defaultTemplate: {
      isShow: false,
      templateType: 'all',
      messageIndex: 0,
    },
    whatsappTemplate: {
      isShow: false,
      templateType: 'all',
      messageIndex: 0,
    },
    isPreview: false,
    isActive: false,
    isLoading: false,
    isShowWaListDialog: false,
    isShowOptionalDialog: false,
    isShowConfirmCardDialog: false,
    isShowImageCardDialog: false,
    isSaveAsDefaultTemplate: false,
    isShowCardDialogTemplateIndex: 0,
    isSettingError: false,
    isErrorNodeIds: [],
    list: [],
    isShowSettingBar: false,
  },
  reducers: {
    setList: (state, action) => ({
      ...state,
      list: action.payload,
    }),
    setIsSettingError: (state, action) => ({
      ...state,
      isSettingError: action.payload,
    }),
    setIsErrorNodeIds: (state, action) => ({
      ...state,
      isErrorNodeIds: action.payload,
    }),
    setHighlightPathNodes: (state, action) => ({
      ...state,
      highlightPathNodes: action.payload,
    }),
    setIsPreview: (state, action) => ({
      ...state,
      isPreview: action.payload,
    }),
    setIsActive: (state, action) => ({
      ...state,
      isActive: action.payload,
    }),
    setIsLoading: (state, action) => ({
      ...state,
      isLoading: action.payload,
    }),
    setSelectedNode: (state, action) => ({
      ...state,
      selectedNode: action.payload,
    }),
    setNodes: (state, action) => ({
      ...state,
      data: {
        ...state?.data,
        nodes: action.payload,
      },
    }),
    setEdges: (state, action) => ({
      ...state,
      data: {
        ...state?.data,
        edges: action.payload,
      },
    }),
    setNodeById: (state, action) => ({
      ...state,
      data: {
        ...state?.data,
        nodes: (state?.data?.nodes || []).map(e =>
          e.id === (action.payload || {})?.id ? action.payload : e,
        ),
      },
    }),
    setData: (state, action) => ({
      ...state,
      data: {
        ...state.data,
        ...action.payload,
      },
    }),
    setState: (state, action) => ({
      ...state,
      ...action.payload,
    }),
    setDefaultTemplate: (state, action) => ({
      ...state,
      defaultTemplate: {
        ...state.defaultTemplate,
        ...action.payload,
      },
      whatsappTemplate: {
        ...state.whatsappTemplate,
        isShow: false,
      },
    }),
    setWhatsappTemplate: (state, action) => ({
      ...state,
      whatsappTemplate: {
        ...state.whatsappTemplate,
        ...action.payload,
      },
      defaultTemplate: {
        ...state.defaultTemplate,
        isShow: false,
      },
    }),
    setIsShowSettingBar: (state, action) => ({
      ...state,
      isShowSettingBar: action.payload,
    }),
  },
});

export const {
  setWhatsappTemplate,
  setList,
  setIsSettingError,
  setIsErrorNodeIds,
  setHighlightPathNodes,
  setIsPreview,
  setIsActive,
  setIsLoading,
  setNodes,
  setEdges,
  setData,
  setState,
  setSelectedNode,
  setNodeById,
  setDefaultTemplate,
  setIsShowSettingBar,
} = mainSlice.actions;

const defaultMessages = [
  {
    index: 0,
    contentType: 'text/plain',
    data: {
      content: '',
    },
  },
];

const defaultScheduleNode = {
  type: NodeType.SCHEDULE,
  data: {
    type: ScheduleType.DAY,
    time: '15:00',
  },
  position: { x: 0, y: 0 },
};

const defaultWaitNode = {
  type: NodeType.WAIT,
  data: {
    waitTime: 300,
  },
  position: { x: 0, y: 0 },
};

const defaultCouponNode = {
  type: NodeType.COUPON,
  data: {
    name: '優惠券',
    skipOOS: false,
    couponId: '',
    couponName: '',
  },
  position: { x: 0, y: 0 },
};

const defaultMessageNode = {
  type: NodeType.MESSAGE,
  data: {
    name: '訊息',
    waitTime: null,
    skipOOS: false,
    messages: defaultMessages,
  },
  position: { x: 0, y: 0 },
};

const defaultTagNode = {
  type: NodeType.TAGGING,
  data: {
    tags: [],
  },
  position: { x: 0, y: 0 },
};

const defaultInboxNode = {
  type: NodeType.INBOX,
  data: {
    inbox: 'unassigned',
    displayName: '',
    userId: '',
  },
  position: { x: 0, y: 0 },
};

const defaultAddEdge = {
  type: EdgeType.ADD,
};

export const getNodeSize = node => {
  const { type } = node || {};
  switch (type) {
    case NodeType.SCHEDULE:
    case NodeType.WAIT:
    case NodeType.MA:
    case NodeType.TAGGING:
    case NodeType.CUSTOMERCONDITION:
    case NodeType.GACONDITION:
    case NodeType.CONDITION:
    case NodeType.TRIGGER:
    case NodeType.INBOX:
      return { width: 240, height: 92 };
    case NodeType.COUPON:
      return { width: 240, height: 120 };
    case NodeType.MESSAGE:
      return { width: 240, height: 160 };
    case NodeType.START:
      return { width: 84, height: 31 };
    case NodeType.END:
      return { width: 68, height: 24 };
    default:
      return { width: 0, height: 0 };
  }
};

export const getLayoutedElements = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({
    labelpos: 'c',
    ranksep: 100,
    nodesep: 100,
    edgesep: 20,
    labeloffset: 20,
    rankdir: 'TB',
  });

  nodes.forEach(node => {
    const { width, height } = getNodeSize(node);
    dagreGraph.setNode(node.id, { width, height });
  });

  edges.forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const tempNodes = nodes.map(el => {
    const e = { ...el };

    const { width, height } = getNodeSize(el);
    const nodeWithPosition = dagreGraph.node(e.id);
    e.targetPosition = 'top';
    e.sourcePosition = 'bottom';

    e.position = {
      x: nodeWithPosition.x - width / 2 + Math.random() / 1000,
      y: nodeWithPosition.y - height / 2,
    };
    e.width = nodeWithPosition?.width;
    e.height = nodeWithPosition?.height;

    return e;
  });

  return {
    nodes: tempNodes.map(el => {
      if (el?.type === NodeType.GROUP) {
        const e = { ...el };
        const groupNodes =
          tempNodes.filter(n => n?.data?.groupId === e?.id) || [];

        const firstGroupNodes = _minBy(groupNodes, n => n?.position?.x) || {};
        const lastGroupNodes = _maxBy(groupNodes, n => n?.position?.x) || {};

        if (firstGroupNodes && lastGroupNodes) {
          e.position = {
            x: firstGroupNodes?.position?.x - 24,
            y: firstGroupNodes?.position?.y - 24,
          };

          const width =
            lastGroupNodes?.position?.x -
            firstGroupNodes?.position?.x +
            48 +
            240;
          const height = 140;

          e.width = width;
          e.height = height;

          e.style = {
            width,
            height,
          };
        }

        return e;
      }
      return el;
    }),
    edges,
  };
};

export const onLayout = (nodes, edges) => async (dispatch, getStore) => {
  const { selectedNode } = getStore().ma;

  const { nodes: newNodes, edges: newEdges } = getLayoutedElements(
    nodes,
    edges,
  );

  if (selectedNode) {
    const newNode = (newNodes || []).find(n => n?.id === selectedNode?.id);
    if (newNode) dispatch(setSelectedNode(newNode));
  }

  dispatch(setNodes(newNodes));
  dispatch(setEdges(newEdges));

  return { nodes: newNodes, edges: newEdges };
};

const changeConditionSource = (nodes, groupId, source) => {
  return nodes.map(n => {
    if (n?.id === groupId || n?.data?.groupId === groupId) {
      return {
        ...n,
        data: {
          ...n?.data,
          source,
        },
      };
    }
    return n;
  });
};

export const addInboxNode = (source, target) => async (dispatch, getStore) => {
  try {
    const { data } = getStore().ma;
    const { nodes, edges } = data || {};
    const targetNode = (nodes || []).find(n => n?.id === target);

    const nodeId = randomText();
    const edgeId = randomText();
    const tempEdges = [...edges].map(e => {
      if (source === e?.source) {
        return { ...e, source: nodeId };
      }
      return e;
    });

    const inboxNode = {
      ...defaultInboxNode,
      id: nodeId,
    };

    const addEdge = {
      ...defaultAddEdge,
      id: edgeId,
      source,
      target: nodeId,
    };

    // 為了排序Nodes必須使用插入
    let newNodes = [...nodes];

    // 若後面是分支則需修改分之上的source id
    if (targetNode?.data?.groupId) {
      newNodes = changeConditionSource(
        newNodes,
        targetNode?.data?.groupId,
        nodeId,
      );
    }

    newNodes.splice(
      newNodes.findIndex(n => n?.id === source) + 1,
      0,
      inboxNode,
    );

    const { nodes: tempNodes } = await dispatch(
      onLayout(newNodes, [...tempEdges, addEdge]),
    );

    const newMessageNode = (tempNodes || []).find(n => n?.id === nodeId);
    if (newMessageNode) dispatch(setSelectedNode(newMessageNode));
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

export const addTagNode = (source, target) => async (dispatch, getStore) => {
  try {
    const { data } = getStore().ma;
    const { nodes, edges } = data || {};
    const targetNode = (nodes || []).find(n => n?.id === target);

    const nodeId = randomText();
    const edgeId = randomText();
    const tempEdges = [...edges].map(e => {
      if (source === e?.source) {
        return { ...e, source: nodeId };
      }
      return e;
    });

    const tagNode = {
      ...defaultTagNode,
      id: nodeId,
    };

    const addEdge = {
      ...defaultAddEdge,
      id: edgeId,
      source,
      target: nodeId,
    };

    // 為了排序Nodes必須使用插入
    let newNodes = [...nodes];

    // 若後面是分支則需修改分之上的source id
    if (targetNode?.data?.groupId) {
      newNodes = changeConditionSource(
        newNodes,
        targetNode?.data?.groupId,
        nodeId,
      );
    }

    newNodes.splice(newNodes.findIndex(n => n?.id === source) + 1, 0, tagNode);

    const { nodes: tempNodes } = await dispatch(
      onLayout(newNodes, [...tempEdges, addEdge]),
    );

    const newMessageNode = (tempNodes || []).find(n => n?.id === nodeId);
    if (newMessageNode) dispatch(setSelectedNode(newMessageNode));
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

export const addMessageNode =
  (source, target) => async (dispatch, getStore) => {
    try {
      const { data } = getStore().ma;
      const { nodes, edges } = data || {};
      const targetNode = (nodes || []).find(n => n?.id === target);

      const messageId = randomText();
      const edgeId = randomText();
      const tempEdges = [...edges].map(e => {
        if (source === e?.source) {
          return { ...e, source: messageId };
        }
        return e;
      });

      const messageNode = {
        ...defaultMessageNode,
        id: messageId,
        data: {
          ...defaultMessageNode?.data,
          name: `訊息 ${
            (nodes || []).filter(n => n?.type === NodeType.MESSAGE).length + 1
          }`,
        },
      };

      const addEdge = {
        ...defaultAddEdge,
        id: edgeId,
        source,
        target: messageId,
      };

      // 為了排序Nodes必須使用插入
      let newNodes = [...nodes];

      // 若後面是分支則需修改分之上的source id
      if (targetNode?.data?.groupId) {
        newNodes = changeConditionSource(
          newNodes,
          targetNode?.data?.groupId,
          messageId,
        );
      }

      newNodes.splice(
        newNodes.findIndex(n => n?.id === source) + 1,
        0,
        messageNode,
      );

      const { nodes: tempNodes } = await dispatch(
        onLayout(newNodes, [...tempEdges, addEdge]),
      );

      const newMessageNode = (tempNodes || []).find(n => n?.id === messageId);
      if (newMessageNode) dispatch(setSelectedNode(newMessageNode));
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

export const deleteNode = nodeId => async (dispatch, getStore) => {
  try {
    const { data, selectedNode } = getStore().ma;
    const { nodes, edges } = data || {};
    let newNodes = nodes.filter(e => e?.id !== nodeId) || [];
    const topEdg = edges.find(n => n?.target === nodeId);
    const bottomEdg = edges.find(n => n?.source === nodeId);
    const bottomNodes = nodes.find(e => e?.id === bottomEdg?.target);

    const newEdges = [...edges]
      .map(e => {
        if (e?.source === nodeId) {
          return { ...e, source: topEdg?.source };
        }
        return e;
      })
      .filter(e => e?.target !== nodeId);

    // 若後面是分支則需修改分之上的source id
    if (
      (bottomNodes?.type === 'condition' ||
        bottomNodes?.type === 'GA_condition') &&
      bottomNodes?.data?.groupId
    ) {
      newNodes = changeConditionSource(
        newNodes,
        bottomNodes?.data?.groupId,
        topEdg?.source,
      );
    }

    if (selectedNode !== null) dispatch(setSelectedNode(null));
    await dispatch(onLayout(newNodes, newEdges));
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

// array => [<node>] or [nodeId] 刪除多modes
export const deleteNodes = array => async (dispatch, getStore) => {
  try {
    const { data, selectedNode } = getStore().ma;
    const { nodes, edges } = data || {};
    const newNodes =
      nodes.filter(e => !array?.find(a => (a?.id || a) === e?.id)) || [];

    let newEdges = [...edges];

    array.forEach(a => {
      const nodeId = a?.id || a;
      const topEdg = edges.find(n => n?.target === nodeId);

      newEdges = newEdges
        .map(e => {
          if (e?.source === nodeId) {
            return { ...e, source: topEdg?.source };
          }
          return e;
        })
        .filter(e => e?.target !== nodeId);
    });

    if (selectedNode !== null) dispatch(setSelectedNode(null));
    await dispatch(onLayout(newNodes, newEdges));
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

export const checkPlatform = (platform, integration) => {
  return !!integration?.find(i => i?.platform?.name === platform);
};

export const checkGa4EventDomain = (platform, ga4EventDomain, nodes) => {
  const allGACondition = nodes.filter(n => n.type === NodeType.GACONDITION);
  const hasGACondition = !!allGACondition.length;
  const triggerNode = nodes.find(n => n?.type === NodeType.TRIGGER);
  const triggerHasGA =
    triggerNode?.data?.type === GATriggerType.GAADDTOCART ||
    triggerNode?.data?.type === GATriggerType.GAVIEW ||
    triggerNode?.data?.type === GATriggerType.GAPURCHASE ||
    triggerNode?.data?.type === GATriggerType.GAREMOVEFROMCART;

  return platform === PlatformType.LIVECHAT && (triggerHasGA || hasGACondition)
    ? !!ga4EventDomain
    : true;
};

export const checkIsEnabledECSetting = (isEnabledSetting, nodes) => {
  const triggerNode = nodes.find(n => n?.type === NodeType.TRIGGER) || {};
  if (
    [
      ProviderType.SHOPLINE,
      ProviderType.SHOPIFY,
      ProviderType.CYBERBIZ,
    ].includes(triggerNode?.type)
  ) {
    return isEnabledSetting;
  }

  return true;
};

export const checkNode = node => {
  let isVerify = true;
  const { data, type, platform } = node;

  switch (type) {
    case NodeType.GROUP:
      const { type: dataType, packageId, stickerIds } = data || {};

      // 客戶行為
      if (dataType === ConditionsType.STICKER) {
        if (
          !packageId ||
          (stickerIds?.length !== 0 ? !stickerIds?.every(s => !!s) : false)
        ) {
          isVerify = false;
        }
      }

      // 客戶屬性
      switch (data?.triggerType) {
        case CustomerConditionType.LOCATION:
          if (!data?.triggerCondition?.length) {
            isVerify = false;
          }
          break;
        default:
          break;
      }

      return isVerify;
    case NodeType.TRIGGER:
      const { conditions, type: triggerType, action } = data || {};

      switch (triggerType) {
        case ProviderType.SHOPLINE:
        case ProviderType.SHOPIFY:
        case ProviderType.CYBERBIZ:
          switch (action) {
            case ProviderActionType.MEMBERSHIPTIER:
              if (triggerType === ProviderType.CYBERBIZ) {
                if (!conditions?.id) {
                  isVerify = false;
                }
              } else if (!conditions?.level) {
                isVerify = false;
              }

              break;
            case ProviderActionType.TOTALORDERCOUNT:
              if (!conditions) {
                isVerify = false;
              }
              break;
            default:
              break;
          }
          break;
        case TriggerType.KEYWORD:
        case TriggerType.TAG:
          if (
            (conditions || []).some(c =>
              typeof c?.value === 'object'
                ? !_compact(c?.value)?.length
                : false,
            )
          ) {
            isVerify = false;
          }
          break;
        case GATriggerType.GAVIEW:
          if (
            (conditions || []).some(
              c =>
                !(c?.urlType === UrlFilterType.EQ
                  ? c?.url && validateHttpsAndHttpPath(c?.url)
                  : c?.url),
            )
          ) {
            isVerify = false;
          }
          break;
        default:
          break;
      }

      return isVerify;
    case NodeType.MESSAGE:
      if (
        !(
          validateText(data?.name) && validateMessages(data?.messages, platform)
        )
      ) {
        isVerify = false;
      }

      return isVerify;
    case NodeType.COUPON:
      if (!(validateText(data?.name) && validateText(data?.couponId))) {
        isVerify = false;
      }

      return isVerify;
    case NodeType.CONDITION:
      const { buttons, type: buttonsType } = data || {};

      if (buttonsType === 'click' && buttons && (buttons || []).length === 0) {
        isVerify = false;
      }

      return isVerify;
    case NodeType.GACONDITION:
      const { url, urlType } = data || {};

      if (url === undefined) {
        isVerify = true;
      } else if (
        urlType === UrlFilterType.EQ
          ? !(url && validateHttpsAndHttpPath(url))
          : !url
      ) {
        isVerify = false;
      }

      return isVerify;
    case NodeType.TAGGING:
      const { tags } = data || {};

      if (!tags || !tags.length) {
        isVerify = false;
      }

      return isVerify;
    case NodeType.INBOX:
      const { inbox, userId } = data || {};

      if (inbox === 'private' && !userId) {
        isVerify = false;
      }

      return isVerify;
    default:
      return isVerify;
  }
};

const checkNodes = (nodes, platform, dispatch) => {
  const temp = nodes.filter(
    n =>
      n?.type === NodeType.GROUP ||
      n?.type === NodeType.MESSAGE ||
      n?.type === NodeType.TAGGING ||
      n?.type === NodeType.INBOX ||
      n?.type === NodeType.CONDITION ||
      n?.type === NodeType.GACONDITION ||
      n?.type === NodeType.TRIGGER ||
      n?.type === NodeType.COUPON,
  );
  let isVerify = true;
  const ids = [];

  temp.forEach(n => {
    const { id, type, data } = n;

    if (!checkNode({ ...n, platform })) {
      isVerify = false;
      if (type === NodeType.CONDITION || type === NodeType.GACONDITION) {
        ids.push(data?.groupId);
      } else {
        ids.push(id);
      }
    }
  });

  if (dispatch) dispatch(setIsErrorNodeIds(ids));

  return isVerify;
};

const checkSetting = (data, isActive, dispatch) => {
  const { name, startTime } = data || {};
  const isVerify = isActive
    ? validateText(name)
    : validateText(name) && validateDate(moment(), moment(startTime));
  dispatch(setIsSettingError(!isVerify));
  return isVerify;
};

export const getData = objectId => async (dispatch, getStore) => {
  try {
    const { selectedNode } = getStore().ma;

    dispatch(setIsLoading(true));

    const response = await MainService.getData(objectId);

    if (response?.id) {
      dispatch(setData(response));

      dispatch(
        setIsActive(
          moment(response?.startTime).isBefore(moment()) &&
            response?.status !== MAStatusType.EDIT,
        ),
      );

      if (response?.nodes && response?.edges) {
        if (selectedNode !== null) dispatch(setSelectedNode(null));
        dispatch(onLayout(response?.nodes, response?.edges));
      }
    }

    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setData({}));
    dispatch(setIsLoading(false));
    dispatch(errorHandle(error));
  }
};

export const saveData = (orgId, procedureId) => async (dispatch, getStore) => {
  try {
    dispatch(setIsLoading(true));

    const { data, isActive } = getStore().ma;
    const { integration, ga4EventDomain } = getStore().organization;
    const { isEnabledSetting } = getStore().superMarket.EC;
    const { nodes, platform } = data || {};

    const missActionNode = !nodes.find(
      n =>
        n?.type === NodeType.MESSAGE ||
        n?.type === NodeType.COUPON ||
        n?.type === NodeType.TAGGING ||
        n?.type === NodeType.INBOX ||
        n?.type === NodeType.MA,
    );

    if (!missActionNode) {
      const isSettingsVerify = checkSetting(data, isActive, dispatch);
      const isNodesVerify = checkNodes(nodes, platform, dispatch);
      const isPlatformVerify = checkPlatform(platform, integration);
      const isGa4EventVerify = checkGa4EventDomain(
        platform,
        ga4EventDomain,
        nodes,
      );
      const isEnabledECSettingVerify = checkIsEnabledECSetting(
        isEnabledSetting,
        nodes,
      );

      if (
        isGa4EventVerify &&
        isPlatformVerify &&
        isSettingsVerify &&
        isNodesVerify &&
        isEnabledECSettingVerify
      ) {
        await MainService.saveData(orgId, procedureId, {
          enabled: data?.enabled,
          name: data?.name,
          fbTag: data?.fbTag,
          platform: data?.platform,
          startTime: moment(data?.startTime).format('YYYY-MM-DD HH:mm'),
          endTime: moment(data?.endTime).format('YYYY-MM-DD HH:mm'),
          limits: data?.limits,
          oos: data?.oos,
          nodes: data?.nodes.map(n => {
            if (n?.type === NodeType.MESSAGE) {
              return {
                ...n,
                data: {
                  ...n?.data,
                  waitTime: n?.data?.waitTime === 0 ? null : n?.data?.waitTime,
                },
              };
            }
            return n;
          }),
          edges: data?.edges,
        });

        dispatch(
          enqueueSnackbar({
            variant: SnackBarType.SUCCESS,
            message: i18n.t(`success-save`),
          }),
        );

        await dispatch(getData(procedureId));
      } else if (!isEnabledECSettingVerify) {
        dispatch(errorHandle('enabledECSettingError'));
      } else if (!isPlatformVerify) {
        dispatch(errorHandle('platformError'));
      } else if (!isGa4EventVerify) {
        dispatch(errorHandle('ga4Error'));
      } else if (
        !isSettingsVerify &&
        !validateDate(moment(), moment(data?.startTime))
      ) {
        dispatch(errorHandle('data-error-date'));
      } else {
        dispatch(errorHandle('data-error'));
      }
    } else {
      dispatch(errorHandle('data-error-need-message'));
    }

    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setIsLoading(false));
    dispatch(errorHandle(error));
  }
};

export const setNodeData = data => async (dispatch, getStore) => {
  const { selectedNode } = getStore().ma;
  const newNode = {
    ...selectedNode,
    data: { ...selectedNode?.data, ...data },
  };

  dispatch(setSelectedNode(newNode));
  dispatch(setNodeById(newNode));
};

export const setDataByNodeId = (nodeId, data) => async (dispatch, getStore) => {
  const { nodes } = getStore().ma?.data || {};
  const node = nodes.find(n => n?.id === nodeId);
  if (node) {
    const newNode = {
      ...node,
      data: { ...node?.data, ...data },
    };

    await dispatch(setNodeById(newNode));
    return newNode;
  }

  return null;
};

export const addMessage = () => async (dispatch, getStore) => {
  const { selectedNode } = getStore().ma;
  const newNode = {
    ...selectedNode,
    data: {
      ...selectedNode?.data,
      messages: [
        ...(selectedNode?.data?.messages ?? []),
        {
          index: (selectedNode?.data?.messages ?? []).length,
          contentType: 'text/plain',
          data: {
            content: '',
          },
        },
      ],
    },
  };

  dispatch(setSelectedNode(newNode));
  dispatch(setNodeById(newNode));
};

export const deleteMessage = index => async (dispatch, getStore) => {
  const { selectedNode } = getStore().ma;
  const newNode = {
    ...selectedNode,
    data: {
      ...selectedNode?.data,
      messages: (selectedNode?.data?.messages ?? [])
        .filter((message, messageIndex) => messageIndex !== index)
        .map((message, messageIndex) => {
          return {
            ...message,
            index: messageIndex,
          };
        }),
    },
  };

  dispatch(setSelectedNode(newNode));
  dispatch(setNodeById(newNode));
};

export const upMessage = index => async (dispatch, getStore) => {
  const { selectedNode } = getStore().ma;
  const messages = [...(selectedNode?.data?.messages ?? [])];
  const tempMessage = messages[index - 1];
  messages[index - 1] = messages[index];
  messages[index] = tempMessage;

  const newNode = {
    ...selectedNode,
    data: {
      ...selectedNode?.data,
      messages: messages.map((message, messageIndex) => {
        return {
          ...message,
          index: messageIndex,
        };
      }),
    },
  };

  dispatch(setSelectedNode(newNode));
  dispatch(setNodeById(newNode));
};

export const downMessage = index => async (dispatch, getStore) => {
  const { selectedNode } = getStore().ma;
  const messages = [...(selectedNode?.data?.messages ?? [])];
  const tempMessage = messages[index + 1];
  messages[index + 1] = messages[index];
  messages[index] = tempMessage;

  const newNode = {
    ...selectedNode,
    data: {
      ...selectedNode?.data,
      messages: messages.map((message, messageIndex) => {
        return {
          ...message,
          index: messageIndex,
        };
      }),
    },
  };

  dispatch(setSelectedNode(newNode));
  dispatch(setNodeById(newNode));
};

const getAllIncomers = (node, nodes, edges) => {
  if (node) {
    return getIncomers(node, nodes, edges).reduce(
      (memo, incomer) => [
        ...memo,
        incomer,
        ...getAllIncomers(incomer, nodes, edges),
      ],
      [],
    );
  }
  return [];
};

const getAllOutgoers = (node, nodes, edges) => {
  if (node) {
    return getOutgoers(node, nodes, edges).reduce(
      (memo, outgoer) => [
        ...memo,
        outgoer,
        ...getAllOutgoers(outgoer, nodes, edges),
      ],
      [],
      nodes,
      edges,
    );
  }
  return [];
};

// 預覽抓出需要highlight的node
export const highlightPath = () => async (dispatch, getStore) => {
  const { selectedNode, data } = getStore().ma;
  const { nodes, edges } = data || {};
  const newNodes = [];
  let iqnoreNodes = [];
  const allIncomers = getAllIncomers(selectedNode, nodes, edges) || [];
  const allOutgoers = getAllOutgoers(selectedNode, nodes, edges) || [];

  allIncomers.reverse().forEach(n => newNodes.push(n));
  newNodes.push(selectedNode);

  allOutgoers.forEach(n => {
    const isIgnore = iqnoreNodes.find(node => node?.id === n?.id);

    if (n?.data?.groupId) {
      if (
        !isIgnore &&
        !newNodes.find(node => node?.data?.groupId === n?.data?.groupId)
      ) {
        newNodes.push(n);
      } else {
        const outgoers = getAllOutgoers(n, nodes, edges) || [];
        iqnoreNodes = [...iqnoreNodes, ...outgoers];
      }
    } else if (!isIgnore) {
      newNodes.push(n);
    }
  });

  dispatch(setHighlightPathNodes(newNodes));
};

// messageId => 除了刪除進入條件外，連同target發送訊息一併刪除
export const deleteConditionsNode =
  (groupId, messageId) => async (dispatch, getStore) => {
    try {
      const { data } = getStore().ma;
      const { nodes = [], edges = [] } = data || {};

      const end1Id = randomText();
      const edgeId = randomText();

      const groupNode = nodes.find(n => n?.id === groupId);

      const sourceNode = nodes.find(n => n?.id === groupNode?.data?.source);

      const targetEdge = edges.find(e => e?.target === messageId);

      const allOutgoersNodes = getAllOutgoers(sourceNode, nodes, edges) || [];
      const allOutgoersNodesIds = allOutgoersNodes.map(n => n?.id);
      const allOutgoersGroupNodesIds = allOutgoersNodes.map(
        n => n?.data?.groupId,
      );

      const newNodes = nodes.filter(
        n =>
          !~[
            messageId,
            ...allOutgoersNodesIds,
            ...allOutgoersGroupNodesIds,
          ].indexOf(n?.id) && n?.id !== groupId,
      );
      const newEdges = edges.filter(
        e => !~[messageId, ...allOutgoersNodesIds].indexOf(e?.target),
      );

      const endNode = {
        id: end1Id,
        type: NodeType.END,
        position: { x: 0, y: 0 },
      };
      const addEdge = {
        type: EdgeType.ADD,
        id: edgeId,
        source: messageId ? targetEdge?.source : groupNode?.data?.source,
        target: end1Id,
      };

      const sourceNodeIndex = _findLastIndex(
        newNodes,
        n => n?.id === groupNode?.data?.source,
      );

      // 為了排序Edges必須使用插入
      newEdges.splice(
        _findLastIndex(newEdges, n => n?.target === groupNode?.data?.source) +
          1,
        0,
        addEdge,
      );

      // 為了排序Nodes必須使用插入
      newNodes.splice(sourceNodeIndex + 1, 0, endNode);
      await dispatch(onLayout(newNodes, newEdges));
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

// 進入條件 - 任一選項
export const addAnyConditionsNode =
  (source, groupId = randomText()) =>
  async (dispatch, getStore) => {
    try {
      const { data } = getStore().ma;
      const { nodes = [], edges = [] } = data || {};

      const condition1Id = randomText();
      const condition2Id = randomText();
      const end1Id = randomText();
      const end2Id = randomText();
      const edge1Id = randomText();
      const edge2Id = randomText();
      const edge3Id = randomText();
      const edge4Id = randomText();

      const sourceNode = nodes.find(n => n?.id === source) || {};
      let groupNode = nodes.find(n => n?.id === groupId) || {};

      const allOutgoersNodes = getAllOutgoers(sourceNode, nodes, edges) || [];
      const allOutgoersNodesIds = allOutgoersNodes.map(n => n?.id);
      const allOutgoersGroupNodesIds = allOutgoersNodes.map(
        n => n?.data?.groupId,
      );

      const newNodes = nodes.filter(
        n =>
          !~[...allOutgoersNodesIds, ...allOutgoersGroupNodesIds].indexOf(
            n?.id,
          ),
      );
      const newEdges = edges.filter(
        e => !~allOutgoersNodesIds.indexOf(e?.target),
      );

      groupNode = {
        id: groupId,
        type: NodeType.GROUP,
        data: {
          source,
          type: ConditionsType.CLICK,
          clickType: ConditionsClickType.ANY,
          timeType: groupNode?.data?.timeType || TriggerTimeType.DAY,
          time: groupNode?.data?.time || 86400,
          allowMulti: false,
        },
        position: { x: 0, y: 0 },
        className: 'groupNode',
        style: {
          width: 0,
          height: 0,
        },
      };

      const conditionNodes = [
        {
          type: NodeType.CONDITION,
          position: { x: 0, y: 0 },
          id: condition1Id,
          data: {
            groupId,
            source,
            type: 'click',
            buttons: 'any',
          },
        },
        {
          id: end1Id,
          type: NodeType.END,
          position: { x: 0, y: 0 },
        },
        {
          type: NodeType.CONDITION,
          position: { x: 0, y: 0 },
          id: condition2Id,
          data: {
            groupId,
            source,
            type: 'n-click',
          },
        },
        {
          id: end2Id,
          type: NodeType.END,
          position: { x: 0, y: 0 },
        },
      ];

      const addEdges = [
        {
          type: EdgeType.SMOOTHSTEP,
          id: edge1Id,
          source,
          target: condition1Id,
        },
        {
          type: EdgeType.ADD,
          id: edge2Id,
          source: condition1Id,
          target: end1Id,
        },
        {
          type: EdgeType.SMOOTHSTEP,
          id: edge3Id,
          source,
          target: condition2Id,
        },
        {
          type: EdgeType.ADD,
          id: edge4Id,
          source: condition2Id,
          target: end2Id,
        },
      ];

      // 為了排序Nodes必須使用插入
      newNodes.splice(
        newNodes.findIndex(n => n?.id === source) + 1,
        0,
        ...[groupNode, ...conditionNodes],
      );

      const { nodes: tempNodes } = await dispatch(
        onLayout(newNodes, [...newEdges, ...addEdges]),
      );

      const newNode = (tempNodes || []).find(n => n?.id === groupId);
      if (newNode) dispatch(setSelectedNode(newNode));
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

// 進入條件 - 根據卡片選項
export const addButtonConditionsNode =
  (source, groupId = randomText()) =>
  async (dispatch, getStore) => {
    try {
      const { data } = getStore().ma;
      const { nodes = [], edges = [] } = data || {};

      const sourceNode = nodes.find(n => n?.id === source);
      let groupNode = nodes.find(n => n?.id === groupId) || {};

      const allOutgoersNodes = getAllOutgoers(sourceNode, nodes, edges) || [];
      const allOutgoersNodesIds = allOutgoersNodes.map(n => n?.id);
      const allOutgoersGroupNodesIds = allOutgoersNodes.map(
        n => n?.data?.groupId,
      );

      const newNodes = nodes.filter(
        n =>
          !~[...allOutgoersNodesIds, ...allOutgoersGroupNodesIds].indexOf(
            n?.id,
          ),
      );
      const newEdges = edges.filter(
        e => !~allOutgoersNodesIds.indexOf(e?.target),
      );

      groupNode = {
        id: groupId,
        type: NodeType.GROUP,
        data: {
          source,
          type: ConditionsType.CLICK,
          clickType: ConditionsClickType.BUTTON,
          timeType: groupNode?.data?.timeType || TriggerTimeType.DAY,
          time: groupNode?.data?.time || 86400,
          allowMulti: false,
        },
        position: { x: 0, y: 0 },
        className: 'groupNode',
        style: {
          width: 0,
          height: 0,
        },
      };

      const conditionNodes = [];
      const addEdges = [];

      const condition1Id = randomText();
      const end1Id = randomText();
      const edge1Id = randomText();
      const edge2Id = randomText();
      conditionNodes.push({
        type: NodeType.CONDITION,
        position: { x: 0, y: 0 },
        id: condition1Id,
        data: {
          groupId,
          source,
          buttons: [],
          type: 'click',
        },
      });
      conditionNodes.push({
        id: end1Id,
        type: NodeType.END,
        position: { x: 0, y: 0 },
      });
      addEdges.push({
        type: EdgeType.SMOOTHSTEP,
        id: edge1Id,
        source,
        target: condition1Id,
      });
      addEdges.push({
        type: EdgeType.ADD,
        id: edge2Id,
        source: condition1Id,
        target: end1Id,
      });

      const condition2Id = randomText();
      const end2Id = randomText();
      const edge3Id = randomText();
      const edge4Id = randomText();
      conditionNodes.push({
        type: NodeType.CONDITION,
        position: { x: 0, y: 0 },
        id: condition2Id,
        data: {
          groupId,
          source,
          type: 'n-click',
        },
      });
      conditionNodes.push({
        id: end2Id,
        type: NodeType.END,
        position: { x: 0, y: 0 },
      });
      addEdges.push({
        type: EdgeType.SMOOTHSTEP,
        id: edge3Id,
        source,
        target: condition2Id,
      });
      addEdges.push({
        type: EdgeType.ADD,
        id: edge4Id,
        source: condition2Id,
        target: end2Id,
      });

      // 為了排序Nodes必須使用插入
      newNodes.splice(
        newNodes.findIndex(n => n?.id === source) + 1,
        0,
        ...[groupNode, ...conditionNodes],
      );

      const { nodes: tempNodes } = await dispatch(
        onLayout(newNodes, [...newEdges, ...addEdges]),
      );
      const newNode = (tempNodes || []).find(n => n?.id === groupId);
      if (newNode) dispatch(setSelectedNode(newNode));
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

// 新增分支
export const addConditionNode = groupId => async (dispatch, getStore) => {
  try {
    const { data } = getStore().ma;
    const { nodes = [], edges = [] } = data || {};
    const groupNode = nodes.find(n => n?.id === groupId) || {};

    const { source } = groupNode?.data || {};

    const newNodes = [...nodes];
    const newEdges = [...edges];
    const tempNodes = [];
    const tempEdges = [];

    const condition1Id = randomText();
    const end1Id = randomText();
    const edge1Id = randomText();
    const edge2Id = randomText();
    tempNodes.push({
      type: NodeType.CONDITION,
      position: { x: 0, y: 0 },
      id: condition1Id,
      data: {
        groupId,
        source,
        buttons: [],
        type: 'click',
      },
    });
    tempNodes.push({
      id: end1Id,
      type: NodeType.END,
      position: { x: 0, y: 0 },
    });
    tempEdges.push({
      type: EdgeType.SMOOTHSTEP,
      id: edge1Id,
      source,
      target: condition1Id,
    });
    tempEdges.push({
      type: EdgeType.ADD,
      id: edge2Id,
      source: condition1Id,
      target: end1Id,
    });

    const lastConditionNodeIndex = _findLastIndex(
      newNodes,
      n => n?.data?.groupId === groupId && n?.data?.type === 'n-click',
    );
    const lastConditionNodeId = newNodes[lastConditionNodeIndex]?.id;

    // 為了排序Edges必須使用插入
    newEdges.splice(
      _findLastIndex(newEdges, n => n?.target === lastConditionNodeId),
      0,
      ...tempEdges,
    );

    // 為了排序Nodes必須使用插入
    newNodes.splice(lastConditionNodeIndex, 0, ...tempNodes);

    await dispatch(onLayout(newNodes, newEdges));
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

// 刪除單一分支
export const deleteConditionNode = nodeId => async (dispatch, getStore) => {
  try {
    const { data } = getStore().ma;
    const { nodes, edges } = data || {};
    const targetNode = nodes.find(n => n?.id === nodeId);

    const allOutgoersNodes = getAllOutgoers(targetNode, nodes, edges) || [];
    const allOutgoersNodesIds = allOutgoersNodes.map(n => n?.id);
    const allOutgoersGroupNodesIds = allOutgoersNodes.map(
      n => n?.data?.groupId,
    );

    const newNodes = nodes.filter(
      n =>
        !~[...allOutgoersNodesIds, ...allOutgoersGroupNodesIds].indexOf(
          n?.id,
        ) && n?.id !== nodeId,
    );
    const newEdges = edges.filter(
      e =>
        !~[nodeId, ...allOutgoersNodesIds].indexOf(e?.target) &&
        !~[nodeId, ...allOutgoersNodesIds].indexOf(e?.source),
    );

    dispatch(onLayout(newNodes, newEdges));
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

// 更新發送訊息時，確認下方是否為進入條件，若為進入條件則刪除之
export const changeMessageCheckCondition =
  (targetNodeId, callback = () => {}) =>
  async (dispatch, getStore) => {
    try {
      const { data } = getStore().ma;
      const { nodes = [], edges = [] } = data || {};
      const sourceIds = edges
        .filter(e => e?.source === targetNodeId)
        .map(e => e?.target);
      const sourceNodes = nodes.filter(n => !!~sourceIds.indexOf(n?.id));

      const hasConditionNode = sourceNodes.some(
        n => n?.type === NodeType.CONDITION,
      );

      if (hasConditionNode) {
        const groupNode = nodes.find(
          n => n?.id === sourceNodes?.[0]?.data?.groupId,
        );
        if (groupNode?.data?.type === ConditionsType.CLICK) {
          dispatch(
            openModal({
              modalType: ModalType.DIALOG_TEXT,
              modalProps: {
                title: i18n.t('modify-conditions-modal.title'),
                content: i18n.t('modify-conditions-modal.content'),
                cancelText: i18n.t('modify-conditions-modal.cancelText'),
                submitText: i18n.t('modify-conditions-modal.submitText'),
              },
              onClick: async () => {
                const conditionNode = sourceNodes.find(
                  n => n?.type === NodeType.CONDITION,
                );
                if (conditionNode?.data?.groupId) {
                  await dispatch(
                    deleteConditionsNode(conditionNode?.data?.groupId),
                  );
                }
                callback();
              },
            }),
          );
        } else {
          callback();
        }
      } else {
        callback();
      }
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

// FB INSTAGRAM 不支援多頁大圖 圖文訊息，清除所有多頁大圖 圖文訊息資料
export const clearImageMessage = () => async (dispatch, getStore) => {
  const { data, selectedNode } = getStore().ma;
  const { nodes } = data || {};

  const groupIds = [];

  const newNodes = [...nodes].map(n => {
    const { type, data: nodeData, id } = n || {};
    if (type === NodeType.MESSAGE) {
      const newMessages = (nodeData?.messages || []).filter(
        msg =>
          msg?.templateType !== 'image' &&
          msg?.data?.templateType !== 'image' &&
          msg?.templateType !== 'image',
      );

      const targetNode = nodes.find(
        nd => nd?.data?.source === id && nd?.type === NodeType.GROUP,
      );

      if (targetNode) {
        groupIds.push(targetNode?.id);
      }

      return {
        ...n,
        data: {
          ...nodeData,
          messages: newMessages.length ? newMessages : defaultMessages,
        },
      };
    }
    return n;
  });

  if (selectedNode !== null) dispatch(setSelectedNode(null));
  dispatch(setNodes(newNodes));

  groupIds.forEach(groupId => {
    dispatch(deleteConditionsNode(groupId));
  });
};

// 除了 LINE LIVECHAT 其他平台不支援自動帶入商品資訊
export const clearAutoTrackProductMessage =
  () => async (dispatch, getStore) => {
    const { data, selectedNode } = getStore().ma;
    const { nodes } = data || {};

    const newNodes = [...nodes].map(n => {
      const { type, data: nodeData } = n || {};
      if (type === NodeType.MESSAGE) {
        const newMessages = (nodeData?.messages || [])?.map(msg => {
          return {
            ...msg,
            data: {
              ...msg?.data,
              autoTrackProduct: undefined,
            },
          };
        });

        return {
          ...n,
          data: {
            ...nodeData,
            messages: newMessages.length ? newMessages : defaultMessages,
          },
        };
      }
      return n;
    });

    if (selectedNode !== null) dispatch(setSelectedNode(null));
    dispatch(setNodes(newNodes));
  };

// 只有whatsapp 支援列表型卡片
export const clearWaListMessage = () => async (dispatch, getStore) => {
  const { data, selectedNode } = getStore().ma;
  const { nodes } = data || {};

  const groupIds = [];

  const newNodes = [...nodes].map(n => {
    const { type, data: nodeData, id } = n || {};
    if (type === NodeType.MESSAGE) {
      const hasWaList = (nodeData?.messages || []).some(
        msg =>
          msg.templateType === 'waList' || msg.data.templateType === 'waList',
      );

      if (hasWaList) {
        const newMessages = (nodeData?.messages || []).filter(
          msg =>
            msg?.data?.templateType !== 'waList' &&
            msg?.templateType !== 'waList',
        );

        const targetNode = nodes.find(
          nd => nd?.data?.source === id && nd?.type === NodeType.GROUP,
        );

        if (targetNode) {
          groupIds.push(targetNode?.id);
        }

        return {
          ...n,
          data: {
            ...nodeData,
            messages: newMessages.length ? newMessages : defaultMessages,
          },
        };
      }
      return n;
    }
    return n;
  });

  if (selectedNode !== null) dispatch(setSelectedNode(null));
  dispatch(setNodes(newNodes));

  groupIds.forEach(groupId => {
    dispatch(deleteConditionsNode(groupId));
  });
};

// FB INSTAGRAM 不支援多頁大圖 圖文訊息，清除所有多頁大圖 圖文訊息資料
export const clearImagemapAndImageMessage =
  () => async (dispatch, getStore) => {
    const { data, selectedNode } = getStore().ma;
    const { nodes } = data || {};

    const groupIds = [];

    const newNodes = [...nodes].map(n => {
      const { type, data: nodeData, id } = n || {};
      if (type === NodeType.MESSAGE) {
        const hasImagemapOrImage = (nodeData?.messages || []).some(
          msg =>
            msg?.contentType === 'application/x-imagemap' ||
            msg.templateType === 'imagemap' ||
            msg.data.templateType === 'imagemap' ||
            msg.templateType === 'image' ||
            msg.data.templateType === 'image',
        );

        if (hasImagemapOrImage) {
          const newMessages = (nodeData?.messages || []).filter(
            msg =>
              msg?.contentType !== 'application/x-imagemap' &&
              msg?.data?.templateType !== 'imagemap' &&
              msg?.templateType !== 'imagemap' &&
              msg?.data?.templateType !== 'image' &&
              msg?.templateType !== 'image',
          );

          const targetNode = nodes.find(
            nd => nd?.data?.source === id && nd?.type === NodeType.GROUP,
          );

          if (targetNode) {
            groupIds.push(targetNode?.id);
          }

          return {
            ...n,
            data: {
              ...nodeData,
              messages: newMessages.length ? newMessages : defaultMessages,
            },
          };
        }
        return n;
      }
      return n;
    });

    if (selectedNode !== null) dispatch(setSelectedNode(null));
    dispatch(setNodes(newNodes));

    groupIds.forEach(groupId => {
      dispatch(deleteConditionsNode(groupId));
    });
  };

// INSTAGRAM 不支援影片音訊檔案，清除所有影片音訊資料
export const clearMP34FileMessage = () => async (dispatch, getStore) => {
  const { data, selectedNode } = getStore().ma;
  const { nodes } = data || {};

  const newNodes = [...nodes].map(n => {
    const { type, data: nodeData } = n || {};
    if (type === NodeType.MESSAGE) {
      const newMessages = (nodeData?.messages || []).filter(
        msg =>
          msg?.contentType !== 'application/x-audio' &&
          msg?.contentType !== 'application/x-video',
      );

      return {
        ...n,
        data: {
          ...nodeData,
          messages: newMessages.length ? newMessages : defaultMessages,
        },
      };
    }
    return n;
  });

  if (selectedNode !== null) dispatch(setSelectedNode(null));
  dispatch(setNodes(newNodes));
};

// INSTAGRAM 不支電話按鈕，清除所有電話按鈕資料
export const clearPhoneBtnMessage = () => async (dispatch, getStore) => {
  const { data, selectedNode } = getStore().ma;
  const { nodes } = data || {};

  const newNodes = [...nodes].map(n => {
    const { type, data: nodeData } = n || {};
    if (type === NodeType.MESSAGE) {
      const newMessages = (nodeData?.messages || []).map(msg => {
        if (
          msg.contentType === 'application/x-template' &&
          (msg?.data?.elements || []).some(e =>
            (e?.buttons || []).some(b => b?.type === 'phone'),
          )
        ) {
          return {
            ...msg,
            data: {
              ...msg.data,
              elements: msg?.data?.elements.map(e => {
                if ((e?.buttons || []).some(b => b?.type === 'phone')) {
                  return {
                    ...e,
                    buttons: e?.buttons.map(b => {
                      if (b?.type === 'phone') {
                        return {
                          type: 'postback',
                          title: '',
                          data: '',
                          tags: [],
                        };
                      }
                      return b;
                    }),
                  };
                }
                return e;
              }),
            },
          };
        }
        return msg;
      });

      return {
        ...n,
        data: {
          ...nodeData,
          messages: newMessages.length ? newMessages : defaultMessages,
        },
      };
    }
    return n;
  });

  if (selectedNode !== null) dispatch(setSelectedNode(null));
  dispatch(setNodes(newNodes));
};

// 取得 前往其他旅程 所需要的旅程列表
export const getMAList =
  (orgId, platform = PlatformType.LINE) =>
  async dispatch => {
    try {
      dispatch(setIsLoading(true));

      const response = await MainService.getList(orgId, platform);

      dispatch(setList(response));
      dispatch(setIsLoading(false));
    } catch (error) {
      dispatch(setList([]));
      dispatch(setIsLoading(false));
      dispatch(errorHandle(error));
    }
  };

// 新增行為設置
export const addMANode = (source, target) => async (dispatch, getStore) => {
  try {
    const { data, list } = getStore().ma;
    const { nodes, edges } = data || {};
    const maId = randomText();
    const edgeId = randomText();
    const tempEdges = [...edges].map(e => {
      if (source === e?.source) {
        return { ...e, target: maId };
      }
      return e;
    });

    const maNode = {
      id: maId,
      type: NodeType.MA,
      data: {
        type: MANodeType.MA,
        procedureId: (list || [])[0]?.id,
      },
      position: { x: 0, y: 0 },
    };

    const addEdge = {
      type: EdgeType.SMOOTHSTEP,
      id: edgeId,
      source: maId,
      target,
    };

    // 為了排序Nodes必須使用插入
    const newNodes = [...nodes];

    newNodes.splice(newNodes.findIndex(n => n?.id === source) + 1, 0, maNode);

    const { nodes: tempNodes } = await dispatch(
      onLayout(newNodes, [...tempEdges, addEdge]),
    );

    const newNode = (tempNodes || []).find(n => n?.id === maId);
    if (newNode) dispatch(setSelectedNode(newNode));
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

// 取得該Node上下總共串接的數量，若不帶 source 則取所有node內最大上下串接數
export const getMaxFlowCount = source => (dispatch, getStore) => {
  try {
    const { data } = getStore().ma;
    const { nodes, edges } = data || {};
    let allEndNodes = [];

    if (source) {
      const sourceNode = nodes.find(n => n?.id === source);
      const sourceAllOutgoers = getAllOutgoers(sourceNode, nodes, edges);
      allEndNodes = sourceAllOutgoers.filter(n => n?.type === NodeType.END);
    } else {
      allEndNodes = nodes.filter(n => n?.type === NodeType.END);
    }
    return _max(
      allEndNodes.map(n => {
        const allIncomers = getAllIncomers(n, nodes, edges);
        return (allIncomers || []).filter(
          i => i?.type !== NodeType.START && i?.type !== NodeType.END,
        ).length;
      }),
    );
  } catch (error) {
    dispatch(errorHandle(error));
    return null;
  }
};

// GA 事件 - 購物車移除商品、加入購物車、完成結帳
export const addGAConditionsNode =
  (source, triggerType, groupId = randomText()) =>
  async (dispatch, getStore) => {
    try {
      const { data } = getStore().ma;
      const { nodes = [], edges = [] } = data || {};

      const condition1Id = randomText();
      const condition2Id = randomText();
      const end1Id = randomText();
      const end2Id = randomText();
      const edge1Id = randomText();
      const edge2Id = randomText();
      const edge3Id = randomText();
      const edge4Id = randomText();

      const sourceNode = nodes.find(n => n?.id === source) || {};
      let groupNode = nodes.find(n => n?.id === groupId) || {};

      const allOutgoersNodes = getAllOutgoers(sourceNode, nodes, edges) || [];
      const allOutgoersNodesIds = allOutgoersNodes.map(n => n?.id);
      const allOutgoersGroupNodesIds = allOutgoersNodes.map(
        n => n?.data?.groupId,
      );

      const newNodes = nodes.filter(
        n =>
          !~[...allOutgoersNodesIds, ...allOutgoersGroupNodesIds].indexOf(
            n?.id,
          ),
      );
      const newEdges = edges.filter(
        e => !~allOutgoersNodesIds.indexOf(e?.target),
      );

      groupNode = {
        id: groupId,
        type: NodeType.GROUP,
        data: {
          source,
          triggerType,
          timeType: TriggerTimeType.HOUR,
          time: 3600,
        },
        position: { x: 0, y: 0 },
        className: 'groupNode',
        style: {
          width: 0,
          height: 0,
        },
      };

      const conditionNodes = [
        {
          type: NodeType.GACONDITION,
          position: { x: 0, y: 0 },
          id: condition1Id,
          data: {
            groupId,
            source,
            type: 'trigger',
          },
        },
        {
          id: end1Id,
          type: NodeType.END,
          position: { x: 0, y: 0 },
        },
        ...(triggerType !== GATriggerType.GAREMOVEFROMCART
          ? [
              {
                type: NodeType.GACONDITION,
                position: { x: 0, y: 0 },
                id: condition2Id,
                data: {
                  groupId,
                  source,
                  type: 'n-trigger',
                },
              },
              {
                id: end2Id,
                type: NodeType.END,
                position: { x: 0, y: 0 },
              },
            ]
          : []),
      ];

      const addEdges = [
        {
          type: EdgeType.ADD,
          id: edge1Id,
          source,
          target: condition1Id,
        },
        {
          type: EdgeType.ADD,
          id: edge2Id,
          source: condition1Id,
          target: end1Id,
        },
        ...(triggerType !== GATriggerType.GAREMOVEFROMCART
          ? [
              {
                type: EdgeType.ADD,
                id: edge3Id,
                source,
                target: condition2Id,
              },
              {
                type: EdgeType.ADD,
                id: edge4Id,
                source: condition2Id,
                target: end2Id,
              },
            ]
          : []),
      ];

      // 為了排序Nodes必須使用插入
      newNodes.splice(
        newNodes.findIndex(n => n?.id === source) + 1,
        0,
        ...[groupNode, ...conditionNodes],
      );

      const { nodes: tempNodes } = await dispatch(
        onLayout(newNodes, [...newEdges, ...addEdges]),
      );

      const newNode = (tempNodes || []).find(n => n?.id === groupId);
      if (newNode) dispatch(setSelectedNode(newNode));
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

// GA 事件 - 瀏覽頁面
export const addGAViewConditionsNode =
  (source, groupId = randomText()) =>
  async (dispatch, getStore) => {
    try {
      const { data } = getStore().ma;
      const { nodes = [], edges = [] } = data || {};

      const condition1Id = randomText();
      const end1Id = randomText();
      const edge1Id = randomText();
      const edge2Id = randomText();

      const sourceNode = nodes.find(n => n?.id === source) || {};
      let groupNode = nodes.find(n => n?.id === groupId) || {};

      const allOutgoersNodes = getAllOutgoers(sourceNode, nodes, edges) || [];
      const allOutgoersNodesIds = allOutgoersNodes.map(n => n?.id);
      const allOutgoersGroupNodesIds = allOutgoersNodes.map(
        n => n?.data?.groupId,
      );

      const newNodes = nodes.filter(
        n =>
          !~[...allOutgoersNodesIds, ...allOutgoersGroupNodesIds].indexOf(
            n?.id,
          ),
      );
      const newEdges = edges.filter(
        e => !~allOutgoersNodesIds.indexOf(e?.target),
      );

      groupNode = {
        id: groupId,
        type: NodeType.GROUP,
        data: {
          source,
          triggerType: GATriggerType.GAVIEW,
        },
        position: { x: 0, y: 0 },
        className: 'groupNode',
        style: {
          width: 0,
          height: 0,
        },
      };

      const conditionNodes = [
        {
          type: NodeType.GACONDITION,
          position: { x: 0, y: 0 },
          id: condition1Id,
          data: {
            groupId,
            source,
            urlType: UrlFilterType.EQ,
            url: '',
            pageViewTimes: 1,
            staySec: null,
          },
        },
        {
          id: end1Id,
          type: NodeType.END,
          position: { x: 0, y: 0 },
        },
      ];

      const addEdges = [
        {
          type: EdgeType.ADD,
          id: edge1Id,
          source,
          target: condition1Id,
        },
        {
          type: EdgeType.ADD,
          id: edge2Id,
          source: condition1Id,
          target: end1Id,
        },
      ];

      // 為了排序Nodes必須使用插入
      newNodes.splice(
        newNodes.findIndex(n => n?.id === source) + 1,
        0,
        ...[groupNode, ...conditionNodes],
      );

      const { nodes: tempNodes } = await dispatch(
        onLayout(newNodes, [...newEdges, ...addEdges]),
      );

      const newNode = (tempNodes || []).find(n => n?.id === groupId);
      if (newNode) dispatch(setSelectedNode(newNode));
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

// GA 事件 - 瀏覽頁面 -> 新增分支
export const addGAViewConditionNode = groupId => async (dispatch, getStore) => {
  try {
    const { data } = getStore().ma;
    const { nodes = [], edges = [] } = data || {};
    const groupNode = nodes.find(n => n?.id === groupId) || {};

    const { source } = groupNode?.data || {};

    const condition1Id = randomText();
    const end1Id = randomText();
    const edge1Id = randomText();
    const edge2Id = randomText();
    const newNodes = [...nodes];
    const newEdges = [...edges];
    const tempNodes = [
      {
        type: NodeType.GACONDITION,
        position: { x: 0, y: 0 },
        id: condition1Id,
        data: {
          groupId,
          source,
          urlType: UrlFilterType.EQ,
          url: '',
          pageViewTimes: 1,
          staySec: null,
        },
      },
      {
        id: end1Id,
        type: NodeType.END,
        position: { x: 0, y: 0 },
      },
    ];
    const tempEdges = [
      {
        type: EdgeType.ADD,
        id: edge1Id,
        source,
        target: condition1Id,
      },
      {
        type: EdgeType.ADD,
        id: edge2Id,
        source: condition1Id,
        target: end1Id,
      },
    ];

    // 找出最後一個該group的condition node
    const lastConditionNodeIndex = _findLastIndex(
      newNodes,
      n => n?.data?.groupId === groupId,
    );

    // 找出該node後所有序列node(array)
    const allOutgoersNodes =
      getAllOutgoers(newNodes[lastConditionNodeIndex], nodes, edges) || [];

    // 找出最後一個end
    const lastOutgoersNodes = allOutgoersNodes?.findLast(
      n => n?.type === NodeType.END,
    );

    // 為了排序Edges必須使用插入，將最要新增項目加在最後一個end之後
    newEdges.splice(
      _findLastIndex(newEdges, e => e?.target === lastOutgoersNodes?.id) + 1,
      0,
      ...tempEdges,
    );

    // 為了排序Nodes必須使用插入，將最要新增項目加在最後一個end之後
    newNodes.splice(
      _findLastIndex(newNodes, n => n?.id === lastOutgoersNodes?.id) + 1,
      0,
      ...tempNodes,
    );

    await dispatch(onLayout(newNodes, newEdges));
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

// 進入條件 - 顧客發送貼圖
export const addEmojiConditionsNode =
  (source, groupId = randomText()) =>
  async (dispatch, getStore) => {
    try {
      const { data } = getStore().ma;
      const { nodes = [], edges = [] } = data || {};

      const condition1Id = randomText();
      const condition2Id = randomText();
      const condition3Id = randomText();
      const end1Id = randomText();
      const end2Id = randomText();
      const end3Id = randomText();
      const edge1Id = randomText();
      const edge2Id = randomText();
      const edge3Id = randomText();
      const edge4Id = randomText();
      const edge5Id = randomText();
      const edge6Id = randomText();

      const sourceNode = nodes.find(n => n?.id === source) || {};
      let groupNode = nodes.find(n => n?.id === groupId) || {};

      const allOutgoersNodes = getAllOutgoers(sourceNode, nodes, edges) || [];
      const allOutgoersNodesIds = allOutgoersNodes.map(n => n?.id);
      const allOutgoersGroupNodesIds = allOutgoersNodes.map(
        n => n?.data?.groupId,
      );

      const newNodes = nodes.filter(
        n =>
          !~[...allOutgoersNodesIds, ...allOutgoersGroupNodesIds].indexOf(
            n?.id,
          ),
      );
      const newEdges = edges.filter(
        e => !~allOutgoersNodesIds.indexOf(e?.target),
      );

      groupNode = {
        id: groupId,
        type: NodeType.GROUP,
        data: {
          source,
          type: ConditionsType.STICKER,
          packageId: '',
          stickerIds: [],
          timeType: groupNode?.data?.timeType || TriggerTimeType.DAY,
          time: groupNode?.data?.time || 86400,
        },
        position: { x: 0, y: 0 },
        className: 'groupNode',
        style: {
          width: 0,
          height: 0,
        },
      };

      const conditionNodes = [
        {
          type: NodeType.CONDITION,
          position: { x: 0, y: 0 },
          id: condition1Id,
          data: {
            groupId,
            source,
            type: 'right-sticker',
          },
        },
        {
          id: end1Id,
          type: NodeType.END,
          position: { x: 0, y: 0 },
        },
        {
          type: NodeType.CONDITION,
          position: { x: 0, y: 0 },
          id: condition2Id,
          data: {
            groupId,
            source,
            type: 'wrong-sticker',
          },
        },
        {
          id: end2Id,
          type: NodeType.END,
          position: { x: 0, y: 0 },
        },
        {
          type: NodeType.CONDITION,
          position: { x: 0, y: 0 },
          id: condition3Id,
          data: {
            groupId,
            source,
            type: 'n-sticker',
          },
        },
        {
          id: end3Id,
          type: NodeType.END,
          position: { x: 0, y: 0 },
        },
      ];

      const addEdges = [
        {
          type: EdgeType.ADD,
          id: edge1Id,
          source,
          target: condition1Id,
        },
        {
          type: EdgeType.ADD,
          id: edge2Id,
          source: condition1Id,
          target: end1Id,
        },
        {
          type: EdgeType.ADD,
          id: edge3Id,
          source,
          target: condition2Id,
        },
        {
          type: EdgeType.ADD,
          id: edge4Id,
          source: condition2Id,
          target: end2Id,
        },
        {
          type: EdgeType.ADD,
          id: edge5Id,
          source,
          target: condition3Id,
        },
        {
          type: EdgeType.ADD,
          id: edge6Id,
          source: condition3Id,
          target: end3Id,
        },
      ];

      // 為了排序Nodes必須使用插入
      newNodes.splice(
        newNodes.findIndex(n => n?.id === source) + 1,
        0,
        ...[groupNode, ...conditionNodes],
      );

      const { nodes: tempNodes } = await dispatch(
        onLayout(newNodes, [...newEdges, ...addEdges]),
      );

      const newNode = (tempNodes || []).find(n => n?.id === groupId);
      if (newNode) dispatch(setSelectedNode(newNode));
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

// 除了whatsapp其他平台不支援訊息範本
export const clearWhatsappTemplateMessage =
  () => async (dispatch, getStore) => {
    const { data, selectedNode } = getStore().ma;
    const { nodes } = data || {};

    const groupIds = [];

    const newNodes = [...nodes].map(n => {
      const { type, data: nodeData, id } = n || {};
      if (type === NodeType.MESSAGE) {
        const hasWhatsappTemplate = (nodeData?.messages || []).some(
          msg => msg.waTemplateId !== undefined,
        );

        if (hasWhatsappTemplate) {
          const newMessages = (nodeData?.messages || []).filter(
            msg => msg.waTemplateId === undefined,
          );

          const targetNode = nodes.find(
            nd => nd?.data?.source === id && nd?.type === NodeType.GROUP,
          );

          if (targetNode) {
            groupIds.push(targetNode?.id);
          }

          return {
            ...n,
            data: {
              ...nodeData,
              messages: newMessages.length ? newMessages : defaultMessages,
            },
          };
        }
        return n;
      }
      return n;
    });

    if (selectedNode !== null) dispatch(setSelectedNode(null));
    dispatch(setNodes(newNodes));

    groupIds.forEach(groupId => {
      dispatch(deleteConditionsNode(groupId));
    });
  };

export const addCouponNode = (source, target) => async (dispatch, getStore) => {
  try {
    const { data } = getStore().ma;
    const { nodes, edges } = data || {};
    const targetNode = (nodes || []).find(n => n?.id === target);

    const nodeId = randomText();
    const edgeId = randomText();
    const tempEdges = [...edges].map(e => {
      if (source === e?.source) {
        return { ...e, source: nodeId };
      }
      return e;
    });

    const node = {
      ...defaultCouponNode,
      id: nodeId,
      data: {
        ...defaultCouponNode?.data,
        name: `優惠券 ${
          (nodes || []).filter(n => n?.type === NodeType.COUPON).length + 1
        }`,
      },
    };

    const addEdge = {
      ...defaultAddEdge,
      id: edgeId,
      source,
      target: nodeId,
    };

    // 為了排序Nodes必須使用插入
    let newNodes = [...nodes];

    // 若後面是分支則需修改分之上的source id
    if (targetNode?.data?.groupId) {
      newNodes = changeConditionSource(
        newNodes,
        targetNode?.data?.groupId,
        nodeId,
      );
    }

    newNodes.splice(newNodes.findIndex(n => n?.id === source) + 1, 0, node);

    const { nodes: tempNodes } = await dispatch(
      onLayout(newNodes, [...tempEdges, addEdge]),
    );

    const newNode = (tempNodes || []).find(n => n?.id === nodeId);
    if (newNode) dispatch(setSelectedNode(newNode));
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

// 客戶屬性
export const addCustomerConditionsNode =
  (source, groupId = randomText()) =>
  async (dispatch, getStore) => {
    try {
      const { data } = getStore().ma;
      const { nodes = [], edges = [] } = data || {};

      const condition1Id = randomText();
      const condition2Id = randomText();
      const end1Id = randomText();
      const end2Id = randomText();
      const edge1Id = randomText();
      const edge2Id = randomText();
      const edge3Id = randomText();
      const edge4Id = randomText();

      const sourceNode = nodes.find(n => n?.id === source) || {};
      let groupNode = nodes.find(n => n?.id === groupId) || {};

      const allOutgoersNodes = getAllOutgoers(sourceNode, nodes, edges) || [];
      const allOutgoersNodesIds = allOutgoersNodes.map(n => n?.id);
      const allOutgoersGroupNodesIds = allOutgoersNodes.map(
        n => n?.data?.groupId,
      );

      const newNodes = nodes.filter(
        n =>
          !~[...allOutgoersNodesIds, ...allOutgoersGroupNodesIds].indexOf(
            n?.id,
          ),
      );
      const newEdges = edges.filter(
        e => !~allOutgoersNodesIds.indexOf(e?.target),
      );

      groupNode = {
        id: groupId,
        type: NodeType.GROUP,
        data: {
          source,
          triggerType: CustomerConditionType.TAG,
          triggerCondition: [{}],
        },
        position: { x: 0, y: 0 },
        className: 'groupNode',
        style: {
          width: 0,
          height: 0,
        },
      };

      const conditionNodes = [
        {
          type: NodeType.CUSTOMERCONDITION,
          position: { x: 0, y: 0 },
          id: condition1Id,
          data: {
            groupId,
            source,
            type: 'trigger',
          },
        },
        {
          id: end1Id,
          type: NodeType.END,
          position: { x: 0, y: 0 },
        },
        {
          type: NodeType.CUSTOMERCONDITION,
          position: { x: 0, y: 0 },
          id: condition2Id,
          data: {
            groupId,
            source,
            type: 'n-trigger',
          },
        },
        {
          id: end2Id,
          type: NodeType.END,
          position: { x: 0, y: 0 },
        },
      ];

      const addEdges = [
        {
          type: EdgeType.ADD,
          id: edge1Id,
          source,
          target: condition1Id,
        },
        {
          type: EdgeType.ADD,
          id: edge2Id,
          source: condition1Id,
          target: end1Id,
        },
        {
          type: EdgeType.ADD,
          id: edge3Id,
          source,
          target: condition2Id,
        },
        {
          type: EdgeType.ADD,
          id: edge4Id,
          source: condition2Id,
          target: end2Id,
        },
      ];

      // 為了排序Nodes必須使用插入
      newNodes.splice(
        newNodes.findIndex(n => n?.id === source) + 1,
        0,
        ...[groupNode, ...conditionNodes],
      );

      const { nodes: tempNodes } = await dispatch(
        onLayout(newNodes, [...newEdges, ...addEdges]),
      );

      const newNode = (tempNodes || []).find(n => n?.id === groupId);
      if (newNode) dispatch(setSelectedNode(newNode));
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

// 固定排程
export const addScheduleNode =
  (source, target) => async (dispatch, getStore) => {
    try {
      const { data } = getStore().ma;
      const { nodes, edges } = data || {};
      const targetNode = (nodes || []).find(n => n?.id === target);

      const nodeId = randomText();
      const edgeId = randomText();
      const tempEdges = [...edges].map(e => {
        if (source === e?.source) {
          return { ...e, source: nodeId };
        }
        return e;
      });

      const node = {
        ...defaultScheduleNode,
        id: nodeId,
      };

      const addEdge = {
        type: EdgeType.ADD,
        id: edgeId,
        source,
        target: nodeId,
      };

      // 為了排序Nodes必須使用插入
      let newNodes = [...nodes];

      // 若後面是分支則需修改分之上的source id
      if (targetNode?.data?.groupId) {
        newNodes = changeConditionSource(
          newNodes,
          targetNode?.data?.groupId,
          nodeId,
        );
      }

      newNodes.splice(newNodes.findIndex(n => n?.id === source) + 1, 0, node);

      const { nodes: tempNodes } = await dispatch(
        onLayout(newNodes, [...tempEdges, addEdge]),
      );

      const newNode = (tempNodes || []).find(n => n?.id === nodeId);
      if (newNode) dispatch(setSelectedNode(newNode));
    } catch (error) {
      dispatch(errorHandle(error));
    }
  };

// 等候時間
export const addWaitNode = (source, target) => async (dispatch, getStore) => {
  try {
    const { data } = getStore().ma;
    const { nodes, edges } = data || {};
    const targetNode = (nodes || []).find(n => n?.id === target);

    const nodeId = randomText();
    const edgeId = randomText();
    const tempEdges = [...edges].map(e => {
      if (source === e?.source) {
        return { ...e, source: nodeId };
      }
      return e;
    });

    const node = {
      ...defaultWaitNode,
      id: nodeId,
    };

    const addEdge = {
      type: EdgeType.ADD,
      id: edgeId,
      source,
      target: nodeId,
    };

    // 為了排序Nodes必須使用插入
    let newNodes = [...nodes];

    // 若後面是分支則需修改分之上的source id
    if (targetNode?.data?.groupId) {
      newNodes = changeConditionSource(
        newNodes,
        targetNode?.data?.groupId,
        nodeId,
      );
    }

    newNodes.splice(newNodes.findIndex(n => n?.id === source) + 1, 0, node);

    const { nodes: tempNodes } = await dispatch(
      onLayout(newNodes, [...tempEdges, addEdge]),
    );

    const newNode = (tempNodes || []).find(n => n?.id === nodeId);
    if (newNode) dispatch(setSelectedNode(newNode));
  } catch (error) {
    dispatch(errorHandle(error));
  }
};

export default mainSlice.reducer;
